import classNames from 'classnames';
import { JSX, ReactNode } from 'react';
import { MaterialIconName } from '../types/materialIconName';
import { MaterialIcon } from './MaterialIcon';

export const COLORS = [
  'yellow',
  'red',
  'green',
  'orange',
  'gray',
  'blue',
  'purple',
  'gold',
] as const;
type Color = (typeof COLORS)[number];
const SIZES = ['small', 'regular', 'large'] as const;
type Size = (typeof SIZES)[number];

type Props = {
  children: ReactNode;
  color: Color | string | undefined;
  icon?: MaterialIconName;
  size?: Size;
  className?: string;
};

export function Tag(props: Props): JSX.Element {
  return (
    <div
      // eslint-disable-next-line tailwindcss/no-arbitrary-value,tailwindcss/no-custom-classname
      className={classNames(
        'inline-block',
        tagSize(props.size, !!props.icon),
        tagColor(props.color),
        props.className,
      )}
    >
      <div className="flex items-center">
        {props.icon && (
          <div className="mr-1">
            <MaterialIcon px={16} icon={props.icon} />
          </div>
        )}
        {props.children}&#8203;
      </div>
    </div>
  );
}

export function tagColor(color: Color | string | undefined): string {
  const name = COLORS.includes(color as Color) ? color : 'gray';
  // The cream-to-amber band of the HK credit card prototype (CMS-2000); the palette has no flat
  // pair for it, so it takes the campaign gradient, with the orange tag's readable tone.
  if (name === 'gold') {
    return 'text-highlights/orange-dark bg-gradient-to-r from-campaign/yellow-1 to-campaign/orange-1';
  }
  // tailwind safelist: text-highlights/yellow-dark bg-highlights/yellow-light
  // tailwind safelist: text-highlights/red-dark bg-highlights/red-light
  // tailwind safelist: text-highlights/green-dark bg-highlights/green-light
  // tailwind safelist: text-highlights/orange-dark bg-highlights/orange-light
  // tailwind safelist: text-highlights/gray-dark bg-highlights/gray-light
  // tailwind safelist: text-highlights/purple-dark bg-highlights/purple-light
  return `text-highlights/${name}-dark bg-highlights/${name}-light`;
}

export function tagSize(
  size: Size | string | undefined,
  hasIcon?: boolean,
): string {
  const name = SIZES.includes(size as Size) ? size : 'regular';

  return classNames(
    {
      'mhg-alpha-typography-bullet-2 py-spacing/gap/soft-4px px-spacing/padding/soft-8px rounded-radius/button':
        name === 'large',
      'mhg-alpha-typography-tag px-tag/padding-x rounded-[--tag-size]':
        name === 'regular',
      'mhg-alpha-typography-badge px-spacing/padding/soft-8px pt-spacing/gap/soft-4px pb-spacing/padding/soft-4px rounded-radius/button':
        name === 'small',
    },
    {
      'pl-tag/padding-icon': hasIcon,
    },
  );
}
